import { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';
import supabaseClient from '@/api/supabaseClient.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InstructionsModal } from '../components/modals/InstructionsModal/InstructionsModal';
import { OrganizationProfileModal, OrganizationProfileData } from '../components/modals/InstructionsModal/OrganizationProfileModal';

interface AssessmentRow {
  id: string;
  domainId: number;
  category: string;
  status: string;
  action: string;
  completedCount: number;
  totalCount: number;
  canViewResults: boolean;
  dateModified: string;
}

const columnHelper = createColumnHelper<AssessmentRow>();

const AssessmentTablePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assessmentData, setAssessmentData] = useState<AssessmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showOrgProfile, setShowOrgProfile] = useState(false);

  const getCurrentUserId = useCallback(async (): Promise<string> => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    
    if (!user) {
      return '';
    }

    const { data: existingUser, error: fetchError } = await supabaseClient
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .maybeSingle();

    if (fetchError) {
      return '';
    }

    if (existingUser) {
      return existingUser.id;
    }

    const { data: newUser, error: createError } = await supabaseClient
      .from('users')
      .insert({
        auth_id: user.id,
        customer_id: user.id, // Using auth_id as customer_id for now
        name: user.user_metadata?.full_name || user.email || '',
        user_type: 'nonprofit'
      })
      .select('id')
      .maybeSingle();

    if (createError) {
      return '';
    }

    return newUser?.id || '';
  }, []);

  // assessment_users.id is used as user_id in user_assessments
  const getAssessmentUserId = useCallback(async (): Promise<string> => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    
    if (!user) {
      return '';
    }

    const { data: existingUser, error: fetchError } = await supabaseClient
      .from('assessment_users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (fetchError) {
      return '';
    }

    if (existingUser) {
      return existingUser.id;
    }

    const { data: newUser, error: createError } = await supabaseClient
      .from('assessment_users')
      .insert({
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name || user.email || ''
      })
      .select('id')
      .maybeSingle();

    if (createError) {
      return '';
    }

    return newUser?.id || '';
  }, []);

  const checkOrganizationProfile = useCallback(async (userId: string): Promise<boolean> => {
    const { data, error } = await supabaseClient
      .from('organization_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking organization profile:', error);
      return false;
    }

    return !!data;
  }, []);

  const saveOrganizationProfile = useCallback(async (userId: string, profileData: OrganizationProfileData): Promise<boolean> => {
    try {
      const { error } = await supabaseClient
        .from('organization_profiles')
        .upsert({
          user_id: userId,
          customer_name: profileData.customerName,
          customer_email: profileData.customerEmail,
          primary_role: profileData.primaryRole || null,
          organization_name: profileData.organizationName,
          city_state: profileData.cityState,
          area_of_focus: profileData.areaOfFocus,
          tax_exempt_status: profileData.taxExemptStatus || null,
          target_populations: profileData.targetPopulations,
          organization_size: profileData.organizationSize || null,
          how_heard_about: profileData.howHeardAbout || null,
        });

      if (error) {
        console.error('Error saving organization profile:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error saving organization profile:', error);
      return false;
    }
  }, []);

  const loadAssessmentData = useCallback(async () => {
    try {
      setLoading(true);
      const [userId, assessmentUserId] = await Promise.all([
        getCurrentUserId(),
        getAssessmentUserId(),
      ]);
      
      if (!userId || !assessmentUserId) {
        return;
      }

      const { data: domains, error: domainsError } = await supabaseClient
        .from('domains')
        .select(`
          id,
          name,
          subdomains (
            id,
            name
          )
        `)
        .order('id');

      if (domainsError) {
        return;
      }

      const { data: userAssessments, error: assessmentsError } = await supabaseClient
        .from('user_assessments')
        .select(`
          id,
          assessment_name,
          status,
          completed_at,
          created_at,
          user_responses (
            subdomain_id,
            selected_level
          )
        `)
        .eq('user_id', assessmentUserId)
        .order('completed_at', { ascending: false });

      if (assessmentsError) {
        return;
      }

      const tableData: AssessmentRow[] = [];

      for (const domain of domains || []) {
        const subdomains = domain.subdomains || [];
        const totalCount = subdomains.length;

        const domainAssessments = userAssessments?.filter(
          assessment => assessment.assessment_name === domain.name
        ) || [];
        
        const sortedAssessments = domainAssessments.sort((a, b) => {
          if (a.status === 'completed' && b.status !== 'completed') return -1;
          if (b.status === 'completed' && a.status !== 'completed') return 1;
          
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        });
        
        const domainAssessment = sortedAssessments[0] || null;

        let completedCount = 0;
        let isCompleted = false;
        let canViewResults = false;

        if (domainAssessment) {
          const responses = domainAssessment.user_responses || [];
          completedCount = responses.length;
          
          isCompleted = domainAssessment.status === 'completed' || (completedCount === totalCount && domainAssessment.completed_at);
          canViewResults = isCompleted;
        }

        tableData.push({
          id: domainAssessment?.id || '',
          domainId: domain.id,
          category: domain.name,
          status: isCompleted ? t('assessment.completed') : t('assessment.toBeCompleted'),
          action: isCompleted ? t('assessment.review') : t('assessment.start'),
          completedCount,
          totalCount,
          canViewResults,
          dateModified: domainAssessment?.completed_at || domainAssessment?.created_at || ''
        });
      }

      setAssessmentData(tableData);

      // Check if organization profile exists and show modal if not
      const hasProfile = await checkOrganizationProfile(userId);
      const hasSeenOrgProfile = localStorage.getItem('hasSeenOrgProfile');
      
      if (!hasProfile && !hasSeenOrgProfile) {
        setShowOrgProfile(true);
      }

    } catch (error) {
      console.error('Error loading assessment data:', error);
    } finally {
      setLoading(false);
    }
  }, [t, getCurrentUserId, getAssessmentUserId, checkOrganizationProfile]);

  useEffect(() => {
    if (searchParams.get('completed') === 'true') {
      navigate('/assessment', { replace: true });
      loadAssessmentData();
    }
  }, [searchParams, navigate, loadAssessmentData]);

  useEffect(() => {
    loadAssessmentData();
  }, [loadAssessmentData]);

  useEffect(() => {
    const hasSeenOrgProfile = localStorage.getItem('hasSeenOrgProfile');
    const hasSeenInstructions = localStorage.getItem('hasSeenInstructions');

    if (!hasSeenOrgProfile) {
      setShowOrgProfile(true);
    } else if (!hasSeenInstructions) {
      setShowInstructions(true);
      localStorage.setItem('hasSeenInstructions', 'true');
    }
  }, []);

  const handleActionClick = (row: AssessmentRow) => {
    if (row.canViewResults) {
      navigate(`/assessment-answers/${row.id}`);
    } else {
      navigate(`/assessment/${row.domainId}/step/1`);
    }
  };

  const handleOrgProfileSubmit = async (profileData: OrganizationProfileData) => {
    const userId = await getCurrentUserId();
    if (userId) {
      const success = await saveOrganizationProfile(userId, profileData);
      if (success) {
        setShowOrgProfile(false);
        localStorage.setItem('hasSeenOrgProfile', 'true');
        
        // Show instructions modal after successful org profile submission
        const hasSeenInstructions = localStorage.getItem('hasSeenInstructions');
        if (!hasSeenInstructions) {
          setShowInstructions(true);
          localStorage.setItem('hasSeenInstructions', 'true');
        }
      } else {
        alert('There was an error saving your organization profile. Please try again.');
      }
    }
  };

  const handleOrgProfileClose = () => {
    setShowOrgProfile(false);
    localStorage.setItem('hasSeenOrgProfile', 'true');
  
    const hasSeenInstructions = localStorage.getItem('hasSeenInstructions');
    if (!hasSeenInstructions) {
      setShowInstructions(true);
      localStorage.setItem('hasSeenInstructions', 'true');
    }
  };

  const columns = [
    columnHelper.accessor('category', {
      cell: (info) => <span className='font-semibold'>{info.getValue()}</span>,
      header: () => <span>{t('assessment.category')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('status', {
      header: () => t('assessment.status'),
      cell: (info) => (
        <span className={twMerge(
          'font-bold',
          info.getValue() === t('assessment.completed') ? 'text-success' : 'text-warning'
        )}>
          {info.getValue()}
        </span>
      ),
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('action', {
      header: () => t('assessment.action'),
      cell: (info) => (
        <span 
          className='font-bold underline cursor-pointer transition-colors hover:text-blue-600'
          onClick={() => handleActionClick(info.row.original)}
        >
          {info.renderValue()}
        </span>
      ),
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('completedCount', {
      header: () => t('assessment.progress'),
      cell: (info) => (
        <span className="text-sm text-gray-600">
          {info.getValue()} / {info.row.original.totalCount}
        </span>
      ),
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('dateModified', {
      header: () => t('assessment.dateModified'),
      cell: (info) => {
        const dateValue = info.getValue();
        if (!dateValue) return <span className="text-sm text-gray-400">-</span>;
        
        const date = new Date(dateValue);
        return (
          <span className="text-sm text-gray-600">
            {date.toLocaleDateString('en-US')}
          </span>
        );
      },
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
          {t('assessment.title')}
        </div>
        <div className="text-center py-8">{t('assessment.loadingAssessments')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
        {t('assessment.title')}
      </div>

      <DataTable 
        data={assessmentData} 
        columns={columns} 
        className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe" 
      />

      <InstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        onStart={() => setShowInstructions(false)}
      />

      <OrganizationProfileModal
        isOpen={showOrgProfile}
        onClose={handleOrgProfileClose}
        onSubmit={handleOrgProfileSubmit}
      />
    </div>
  );
};

export default AssessmentTablePage;